import React, { ReactElement, useEffect, useState } from "react";
import { LookupsState } from "../../../redux/reducers/getLookups/lookupsReducer";
import { useDispatch, useSelector } from "react-redux";
import { ProfileOverviewState } from "../../../redux/reducers/getProfile/getProfileOverviewReducer";
import { useTranslation } from "react-i18next";
import { getLookups } from "../../../redux/actions/lookups/lookups";
import { getIncomeAttributesBySnapshotId, putIncomeAttributesBySnapshotId } from "../../../redux/actions/incomeAttributes/incomeAttributes";
import { SaveDataState } from "../../../redux/reducers/getSaveData/saveDataReducer";
import { SaveResultState } from "../../../redux/reducers/getSaveData/saveResultReducer";
import Total from "../../../components/common/Total/Total";
import "../PIE.css";
import { useFormErrorContext } from "../../../utils/context/FormErrorContext";
import { useNavigation } from "../../../utils/context/NavigationContext";
import ErrorMessages from "../../../components/common/ErrorMessages/ErrorMessages";
import { IncomeAttributeState } from "../../../redux/reducers/getIncomeAttributes/getIncomeAttributesReducer";
import { accordionDisplay, getCurrentCampaignYear, getLastYearSubmittedCampaign, shouldDisplayPreviousTwoYearAttributes, shouldDisplayPreviousYearAttributes } from "../../../utils/previousIncomeAttributes";
import { MANAGE_PREVIOUS_INCOME_ATTRIBUTES_BY_SNAPSHOT_ID } from "../../../redux/actionTypes/actionsTypes";
import ReserveForReplacementAllowanceTab from "./ReserveForReplacementAllowanceTab";
import IncomeAttributeLookupObject from "../../../types/lookups/IncomeAttributeLookupObject";
import PreviousCampaignIncomeAttributes from "../../../components/common/PreviousCampaignIncomeAttributes/PreviousCampaignIncomeAttributes";
import { selectGetIncomeAttributes, selectLookups, selectMenu, selectPreviousIncomeAttributes, selectProfileGetOverview, selectSaveData, selectSaveResult } from "../../../redux/selectors/exportSelectorPaths";
import { ControlledAccordion, useAccordionProvider } from "@szhsin/react-accordion";
import AccordionItem, { DefaultAccordionProviderOptions } from "../../../components/common/AccordionItem/AccordionItem";

function ReserveForReplacementAllowance(): ReactElement {
  const { t } = useTranslation(['pie', "navigation"]);
  const dispatch = useDispatch();

  const incomeAttributesState: IncomeAttributeState = useSelector(selectGetIncomeAttributes);
  const previousIncomeAttributesState: IncomeAttributeState = useSelector(selectPreviousIncomeAttributes)
  const lookupState: LookupsState = useSelector(selectLookups);
  const menuState = useSelector(selectMenu);
  const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
  const lookupType = profileOverview.profile.profile_type_code.toLowerCase() + "-income-attributes";
  const saveData: SaveDataState = useSelector(selectSaveData);
  const saveResultState: SaveResultState = useSelector(selectSaveResult);
  const [totalExpense, setTotalExpense] = useState<number | undefined>(undefined);
  const { direction } = useNavigation()
  const { errors, clearErrors } = useFormErrorContext()
  const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)
  const isInternal = (sessionStorage.getItem("isInternalUser") as string) === "true"
  const currentCampaignYear = getCurrentCampaignYear()

  const getIncomeAttributes = () => {
    if ((!incomeAttributesState.loading && !incomeAttributesState.success)
      || incomeAttributesState.snapshotId !== Number(sessionStorage.getItem('currentSnapshotId') as string)) {
      dispatch(getIncomeAttributesBySnapshotId(sessionStorage.getItem('currentSnapshotId') as string));
    }

    const campaign = getLastYearSubmittedCampaign(profileOverview.profile)

    if (campaign && ((!previousIncomeAttributesState.loading && !previousIncomeAttributesState.success) || previousIncomeAttributesState.snapshotId !== campaign.currentSnapshotId)) {
      dispatch(getIncomeAttributesBySnapshotId(campaign.currentSnapshotId, MANAGE_PREVIOUS_INCOME_ATTRIBUTES_BY_SNAPSHOT_ID))
    }
  }


  useEffect(() => {
    getIncomeAttributes()
    // test
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!lookupState.success) {
      dispatch(getLookups(lookupType))
    }
  }, [dispatch, lookupState.success, lookupType]);

  const findAttributeData = (attributeCode: string) => {
    let incomeAttributesData: Record<string, unknown> | undefined = undefined;
    if (incomeAttributesState.data) {
      incomeAttributesData = (incomeAttributesState.data as Record<string, unknown>[]).find(({ code }) => code === attributeCode);
    }
    return incomeAttributesData;
  };

  const findPreviousAttributeData = (attributeCode: string, year: string) => {
    let incomeAttributesData: Record<string, unknown> | undefined = undefined;
    const sessionStoragePreviousSnapshotData = sessionStorage.getItem(isInternal ? "previousSnapshotDataInternal" + year : "previousSnapshotData")
    if (sessionStoragePreviousSnapshotData !== null && sessionStoragePreviousSnapshotData !== undefined) {
      let previousSnapshotData = { incomeAttribute: [] }
      previousSnapshotData = JSON.parse(sessionStoragePreviousSnapshotData)
      if (previousSnapshotData.incomeAttribute) {
        incomeAttributesData = (previousSnapshotData.incomeAttribute as Record<string, unknown>[]).find(({ code }) => code === attributeCode);
      }
    }
    return incomeAttributesData;
  }

  const calcTotalExpense = (year: string, previousYearTotal: boolean,) => {
    let calculated = false;
    let total = 0;
    // total = ((findAttributeData("50")?.value as number) + total) || total

    const lookupStateLookups4 = lookupState.lookups[4] as Record<string, IncomeAttributeLookupObject[]>
    const keys = Object.keys(lookupStateLookups4);
    keys.forEach((key) => {
      const lookupAttributes = lookupStateLookups4[key];
      lookupAttributes.forEach((attribute) => {
        if (attribute.activeFlag === true) {
          const code = attribute.incomeAttributeCode;
          let incomeAttributesData = null
          if (previousYearTotal) {
            incomeAttributesData = findPreviousAttributeData(code, year);
          } else {
            incomeAttributesData = findAttributeData(code);
          }
          if (incomeAttributesData && incomeAttributesData.value && incomeAttributesData.value !== 0) {
            total = total + (incomeAttributesData.value as number);
            calculated = true;
          }
        }
      });
    });
    if (calculated) {
      if (previousYearTotal) {
        return total
      } else {
        setTotalExpense(total);
      }
    }
    return undefined
  }

  useEffect(() => {

    if (lookupState.success && incomeAttributesState.success) {
      setTimeout(() => {
        if (totalExpense === undefined) {
          calcTotalExpense(String(currentCampaignYear), false);
        }
      }, 50);
    }
    // eslint-disable-next-line
  }, [incomeAttributesState.data, incomeAttributesState.success, lookupState.lookups, lookupState.success, lookupType, totalExpense]);

  const handleCommentChange = (value: string, name: string) => {
    const idx = name.lastIndexOf("-");
    if (idx !== -1) {
      const code = name.substring(idx + 1);
      const incomeAttributesData = findAttributeData(code);
      const v = value.trim() === "" ? null : value.trim();
      if (incomeAttributesData) {
        incomeAttributesData["description"] = v;
      } else {
        (incomeAttributesState.data as Record<string, unknown>[]).push({
          snapshotId: Number(sessionStorage.getItem("currentSnapshotId") as string),
          code: code,
          description: v,
          value: null,
          percent: null,
          year: null,
          type: "4",
        });
      }
    }
  }

  const handleValueChange = (newAmt: number | undefined | null, name: string, _oldAmt: number | undefined | null) => {
    const idx = name.lastIndexOf("-");
    if (idx !== -1) {
      const code = name.substring(idx + 1);
      const incomeAttributesData = findAttributeData(code);
      const na = (newAmt === undefined || newAmt === null) ? null : newAmt
      if (incomeAttributesData) {
        incomeAttributesData["value"] = na;
      } else {
        (incomeAttributesState.data as Record<string, unknown>[]).push({
          snapshotId: Number(sessionStorage.getItem("currentSnapshotId") as string),
          code: code,
          description: null,
          value: na,
          percent: null,
          year: null,
          type: code === "50" ? "2" : "4",
        });
      }
    }

    calcTotalExpense(String(currentCampaignYear), false)
  }

  useEffect(() => {
    if (saveData.saving) {
      clearErrors()
      const triggerSpectral = isInternal || direction !== "backward"

      const incomeAttributes = (incomeAttributesState.data as Record<string, unknown>[]).filter((data) => data.value !== null || data.description !== null || data.percent !== null);
      dispatch(putIncomeAttributesBySnapshotId(Number(sessionStorage.getItem("currentSnapshotId") as string), {
        "attributes": incomeAttributes,
        "spectral_section": profileOverview.profile.profile_type_code.toLowerCase() + "-" + menuState.activeItem.name,
        "trigger_spectral": triggerSpectral,
        "is_internal": isInternal
      }));
    }
  }, [dispatch, incomeAttributesState.data, menuState.activeItem.name, saveData.saving, profileOverview.profile.profile_type_code, direction, clearErrors, isInternal]);

  useEffect(() => {
    if (saveResultState.status === 200) {
      dispatch(getIncomeAttributesBySnapshotId(Number(sessionStorage.getItem("currentSnapshotId") as string)));
    }
  }, [saveResultState, dispatch]);
  const lookups = lookupState.lookups[4] ? (lookupState.lookups[4] as Record<string, IncomeAttributeLookupObject[]>)["reserve"] : []

  return (
    <>
      <div className="full-width">
        <div className="content-container">
          <ErrorMessages errors={errors} />
          <p className="strong">{t('reserve.caption', { ns: 'pie' })}</p>

          {(!incomeAttributesState.loading && !lookupState.loading && !previousIncomeAttributesState.loading) &&
            <>
              <div className="accordion-root">
                <div className={accordionDisplay(isInternal, "accordion")}>
                  <ControlledAccordion providerValue={providerValue}>
                    <AccordionItem
                      header={t(menuState.activeItem.name, { ns: "navigation" })}
                      ariaLabel={t("reserve.caption", { ns: "pie" }) as string}
                    >
                      <ReserveForReplacementAllowanceTab
                        incomeAttributes={incomeAttributesState.data && ((incomeAttributesState.data as Record<string, unknown>[]).length > 0) ? (incomeAttributesState.data as Record<string, unknown>[]) : undefined}
                        lookups={lookups}
                        handleValueChange={handleValueChange}
                        handleCommentChange={handleCommentChange} />
                    </AccordionItem>
                  </ControlledAccordion>
                </div>
                {shouldDisplayPreviousYearAttributes() ? <>
                  <PreviousCampaignIncomeAttributes lookups={lookups} tabComponent="reserve" forcedTab year={String(currentCampaignYear - 1)} />
                </> : <></>}
                {shouldDisplayPreviousTwoYearAttributes() ? <>
                  <PreviousCampaignIncomeAttributes lookups={lookups} tabComponent="reserve" forcedTab year={String(currentCampaignYear - 2)} />
                </> : <></>}
              </div>
              <div className='total-container'>
                <div className="accordion-root">
                  <div className={accordionDisplay(isInternal, "accordion")}>
                    <Total text={t('reserve.Total')} value={totalExpense} label={true} narrow={(shouldDisplayPreviousYearAttributes() || shouldDisplayPreviousTwoYearAttributes()) ? true : false} />
                  </div>
                  {shouldDisplayPreviousYearAttributes() ? <>
                    <div className={accordionDisplay(isInternal, "right")}>
                      <Total text={t('reserve.Total')} value={calcTotalExpense(String(currentCampaignYear - 1), true)} />
                    </div>
                  </> : <></>}
                  {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <>
                    <div className={accordionDisplay(isInternal, "right")}>
                      <Total text={t('reserve.Total')} value={calcTotalExpense(String(currentCampaignYear - 2), true)} />
                    </div>
                  </> : <></>}
                </div>
              </div>
            </>
          }
        </div>
      </div>
    </>)
}

export default ReserveForReplacementAllowance;
